import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, Plus, Building2, User } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SupplierOption {
  id: string;
  legal_name: string;
  trade_name?: string;
  tax_id?: string;
  phone?: string;
  email?: string;
  entity_type: 'supplier' | 'both';
  customer_type: 'company' | 'individual';
  address_line1?: string;
  address_line2?: string;
  postal_code?: string;
  city?: string;
  province?: string;
  contact_name?: string;
}

interface SupplierSelectorProps {
  value: string | null;
  onValueChange: (id: string | null, supplier?: SupplierOption) => void;
  placeholder?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  onCreateNew?: () => void;
}

export function SupplierSelector({
  value,
  onValueChange,
  placeholder = 'Seleccionar proveedor...',
  disabled = false,
  allowCreate = false,
  onCreateNew,
}: SupplierSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: suppliers = [], isLoading, error } = useQuery<SupplierOption[]>({
    queryKey: ['suppliers-selector'],
    queryFn: async () => {
      const { data, error } = await api
        .from('billing_customers')
        .select('id, legal_name, trade_name, tax_id, phone, email, entity_type, customer_type, address_line1, address_line2, postal_code, city, province, contact_name')
        .in('entity_type', ['supplier', 'both'])
        .eq('is_active', true)
        .order('legal_name');
      if (error) throw error;
      console.log('[SupplierSelector] Loaded', data?.length || 0, 'suppliers');
      return (data || []) as SupplierOption[];
    },
  });

  const filteredSuppliers = useMemo(() => {
    if (!search) return suppliers;
    const term = search.toLowerCase();
    return suppliers.filter(
      (s) =>
        s.legal_name.toLowerCase().includes(term) ||
        s.trade_name?.toLowerCase().includes(term) ||
        s.tax_id?.toLowerCase().includes(term)
    );
  }, [suppliers, search]);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === value),
    [suppliers, value]
  );

  const handleSelect = (supplierId: string) => {
    const supplier = suppliers.find((s) => s.id.toLowerCase() === supplierId.toLowerCase());
    onValueChange(supplierId, supplier);
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onValueChange(null);
    setOpen(false);
  };

  const handleCreateNew = () => {
    setOpen(false);
    onCreateNew?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedSupplier && 'text-muted-foreground'
          )}
        >
          {selectedSupplier ? (
            <div className="flex items-center gap-2 truncate">
              {selectedSupplier.customer_type === 'company' ? (
                <Building2 className="w-4 h-4 shrink-0 text-orange-600" />
              ) : (
                <User className="w-4 h-4 shrink-0 text-orange-600" />
              )}
              <span className="truncate">{selectedSupplier.legal_name}</span>
              {selectedSupplier.tax_id && (
                <span className="text-xs text-muted-foreground">
                  ({selectedSupplier.tax_id})
                </span>
              )}
            </div>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre o NIF..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {error ? (
              <div className="p-4 text-center text-sm text-red-500">
                Error: {(error as Error).message}
              </div>
            ) : isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Cargando proveedores...
              </div>
            ) : filteredSuppliers.length === 0 ? (
              <CommandEmpty>
                No se encontraron proveedores
                {allowCreate && (
                  <Button
                    variant="link"
                    className="mt-2"
                    onClick={handleCreateNew}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crear nuevo proveedor
                  </Button>
                )}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredSuppliers.map((supplier) => (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.id}
                    onSelect={handleSelect}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === supplier.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {supplier.customer_type === 'company' ? (
                        <Building2 className="w-4 h-4 shrink-0 text-orange-600" />
                      ) : (
                        <User className="w-4 h-4 shrink-0 text-orange-600" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium">{supplier.legal_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {supplier.tax_id || supplier.trade_name || 'Sin NIF'}
                        </p>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {allowCreate && filteredSuppliers.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreateNew}
                    className="cursor-pointer text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Crear nuevo proveedor
                  </CommandItem>
                </CommandGroup>
              </>
            )}

            {value && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleClear}
                    className="cursor-pointer text-muted-foreground"
                  >
                    Limpiar seleccion
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
